import './MapTilerMap.styl.css';
import * as maptilersdk from '@maptiler/sdk';

export class MapTilerMap {
    constructor(apiKey, mapContainer, mapStyle, mapCenter, mapZoom) {
        maptilersdk.config.apiKey = apiKey;

        this._mapInitialized = false;

        this._map = new maptilersdk.Map({
            container: mapContainer,
            style: mapStyle,
            center: mapCenter,
            zoom: mapZoom,
        });

        this._staticDataLayers = {};
        this._dynamicDataLayers = {};
        this._customControls = [];

        this._map.on('style.load', async () => {
            // This event is triggered the first the map rendered as well when the map style is changed.
            // Reloading the data layers is necessary, because change the map removes all data layers.
            this._stopDynamicDataInterval();
            this._loadAllDataLayers();
            await this._startDynamicDataInterval();
        });

        this._clickActions = [];

        this._map.on('click', async (e) => {
            await this._runClickActions(e.lngLat);
        });

        this._dynamicDataRefreshRate = 0;
        this._dynamicDataIntervalId = null;

        this._map.on('load', async () => {
            this._mapInitialized = true;
            await this._startDynamicDataInterval();
        });
    }

    addControl(control, position) {
        this._customControls.push({ control: control, position: position });
        this._map.addControl(control, position);
    }

    addStaticDataLayers(id, source, layers) {
        const layersWithSource = layers.map((layer) => ({
            source: id,
            ...layer,
        }));

        this._staticDataLayers[id] = {
            id: id,
            source: source,
            layers: layersWithSource,
        };
    }

    addDynamicDataLayers(id, url, layers) {
        const layersWithSource = layers.map((layer) => ({
            source: id,
            ...layer,
        }));

        this._dynamicDataLayers[id] = {
            id: id,
            url: url,
            layers: layersWithSource,
            data: null,
        };
    }

    addClickAction(action) {
        this._clickActions.push(action);
    }

    setDynamicDataRefreshRate(milliseconds) {
        this._dynamicDataRefreshRate = milliseconds;
    }

    async _startDynamicDataInterval() {
        if (!this._mapInitialized) {
            return false;
        }

        if (this._dynamicDataRefreshRate <= 0) {
            return;
        }

        if (this._dynamicDataIntervalId) {
            clearInterval(this._dynamicDataIntervalId);
        }

        await this._loadDynamicData();
        this._applyDynamicData();

        this._dynamicDataIntervalId = window.setInterval(async () => {
            await this._loadDynamicData();
            this._applyDynamicData();
        }, this._dynamicDataRefreshRate);
    }

    _stopDynamicDataInterval() {
        if (this._dynamicDataIntervalId) {
            clearInterval(this._dynamicDataIntervalId);
        }
    }

    _loadAllDataLayers() {
        Object.values(this._staticDataLayers).forEach((data) => {
            this._map.addSource(data.id, data.source);

            data.layers.forEach((layer) => {
                this._map.addLayer(layer);
            });
        });

        Object.values(this._dynamicDataLayers).forEach((dynamicLayer) => {
            this._map.addSource(dynamicLayer.id, {
                type: 'geojson',
                data: {
                    type: 'FeatureCollection',
                    features: [],
                },
            });

            dynamicLayer.layers.forEach((layer) => {
                this._map.addLayer(layer);
            });
        });
    }

    async _loadDynamicData() {
        await Promise.all(
            Object.entries(this._dynamicDataLayers).map(
                async ([key, dynamicLayer]) => {
                    try {
                        const response = await fetch(dynamicLayer.url);
                        if (response.ok) {
                            this._dynamicDataLayers[key].data =
                                await response.json();
                        }
                    } catch (error) {}
                }
            )
        );
    }

    async _runClickActions(coordinates) {
        for (const action of this._clickActions) {
            await action(this._map, coordinates);
        }
    }

    _applyDynamicData() {
        Object.values(this._dynamicDataLayers).map((dynamicLayer) => {
            if (dynamicLayer.data) {
                this._map.getSource(dynamicLayer.id).setData(dynamicLayer.data);
            }
        });

        let data = Object.values(this._dynamicDataLayers).reduce(
            (acc, dynamicLayer) => {
                acc[dynamicLayer.id] = dynamicLayer.data;
                return acc;
            },
            {}
        );
        this._customControls.forEach((controlInfo) => {
            if (typeof controlInfo.control.updateDynamicData === 'function') {
                controlInfo.control.updateDynamicData(data);
            }
        });
    }
}
